import { formatEuroAmount, getSubscriptionPlan } from '../config/pricing';

export interface TermsSection {
  title: string;
  content: string;
}

export const termsContent: TermsSection[] = [
  {
    title: "1. Privacybeleid",
    content: `
      <p><strong>Laatste update:</strong> 13 januari 2026</p>
      <h4 class="font-semibold text-lg mt-4 mb-2">Welke gegevens verzamelen wij?</h4>
      <ul class="list-disc pl-6 space-y-1">
        <li>Email adres en naam (bij registratie)</li>
        <li>Examenresultaten en voortgang</li>
        <li>IP-adres en device informatie (voor beveiliging)</li>
        <li>Betalingsinformatie (verwerkt via Stripe, niet door ons opgeslagen)</li>
      </ul>
      <h4 class="font-semibold text-lg mt-4 mb-2">Hoe gebruiken wij je gegevens?</h4>
      <p>We gebruiken je gegevens alleen om:</p>
      <ul class="list-disc pl-6 space-y-1">
        <li>Je account te beheren en voortgang bij te houden</li>
        <li>Support te bieden bij technische problemen</li>
        <li>Betalingen te verwerken en abonnementen te beheren</li>
        <li>Het platform te verbeteren op basis van gebruiksdata</li>
      </ul>
      <p class="mt-4"><strong>We delen je gegevens nooit met derden voor marketing doeleinden.</strong></p>
      <h4 class="font-semibold text-lg mt-4 mb-2">GDPR Rechten</h4>
      <p>Je hebt het recht om je gegevens in te zien, te wijzigen of te verwijderen. Neem contact op via <a href="mailto:privacy@inburgertest.nl" class="text-primary hover:underline">privacy@inburgertest.nl</a></p>
    `
  },
  {
    title: "2. Abonnementsvoorwaarden",
    content: `
      <h4 class="font-semibold text-lg mt-4 mb-2">Beschikbare Plannen</h4>
      <ul class="list-disc pl-6 space-y-1">
        <li><strong>Gratis:</strong> gratis - één proefexamen per onderdeel, één poging per onderdeel</li>
        <li><strong>Niveau Bundel:</strong> €${formatEuroAmount(getSubscriptionPlan('level_bundle')?.price ?? 0)}/maand - alle 4 onderdelen voor één niveau (A2 of B1)</li>
        <li><strong>Volledige Toegang:</strong> €${formatEuroAmount(getSubscriptionPlan('full_access')?.price ?? 0)}/maand - alle 4 onderdelen voor zowel A2 als B1</li>
      </ul>
      <h4 class="font-semibold text-lg mt-4 mb-2">Facturering</h4>
      <p>Abonnementen worden automatisch verlengd tot je opzegt. Je ontvangt 7 dagen voor verlenging een herinnering via email.</p>
      <h4 class="font-semibold text-lg mt-4 mb-2">Gratis proefexamen</h4>
      <p>Er is geen proefperiode. Je kunt wel gratis starten met één proefexamen per onderdeel (Lezen, Luisteren, Spreken, Schrijven), met één poging per onderdeel. Voor onbeperkt oefenen is een betaald abonnement nodig.</p>
    `
  },
  {
    title: "3. Opzeggingsbeleid & Herroepingsrecht",
    content: `
      <h4 class="font-semibold text-lg mt-4 mb-2">Opzeggen</h4>
      <p>Je kunt je abonnement <strong>op elk moment opzeggen</strong> via je profielpagina onder "Abonnement Beheren". Er zijn geen extra kosten of verborgen clausules. Je behoudt toegang tot het einde van je huidige factuurperiode.</p>
      <h4 class="font-semibold text-lg mt-4 mb-2">Herroepingsrecht (Bedenktijd)</h4>
      <p>Als consument heb je het wettelijk recht om een overeenkomst voor digitale diensten binnen <strong>14 kalenderdagen</strong> na het sluiten van de overeenkomst te herroepen, zonder opgave van reden (BW 6:230o e.v.).</p>
      <p class="mt-2">Wanneer je bij het afronden van de bestelling expliciet verzoekt om <strong>directe toegang vóór het verstrijken van de herroepingstermijn</strong> en je herroepingsrecht daarmee uitdrukkelijk opgeeft, vervalt het herroepingsrecht zodra de dienst volledig is gestart (BW 6:230p lid 3 sub a). Je geeft dit aan via een aparte bevestiging tijdens het afronden van je aankoop.</p>
      <h4 class="font-semibold text-lg mt-4 mb-2">Terugbetalingen</h4>
      <p>Terugbetalingen worden binnen 14 dagen verwerkt naar je originele betaalmethode, conform BW 6:230s lid 2.</p>
    `
  },
  {
    title: "4. Gegevensgebruik & AVG",
    content: `
      <h4 class="font-semibold text-lg mt-4 mb-2">AVG-naleving</h4>
      <p>Inburgertest voldoet aan de Europese AVG (Algemene Verordening Gegevensbescherming). Je gegevens worden opgeslagen op beveiligde servers binnen de EU.</p>
      <h4 class="font-semibold text-lg mt-4 mb-2">Cookies</h4>
      <p>We gebruiken alleen essentiële cookies voor authenticatie en voorkeuren. Geen trackingcookies van derden of advertentiecookies.</p>
      <h4 class="font-semibold text-lg mt-4 mb-2">Account verwijderen</h4>
      <p>Wanneer je je account verwijdert via je profielpagina, wordt je account <strong>direct gedeactiveerd</strong>. Alle persoonsgegevens worden <strong>binnen 30 dagen definitief verwijderd</strong>, met uitzondering van factuurgegevens die wij 7 jaar bewaren conform de fiscale bewaarplicht.</p>
      <h4 class="font-semibold text-lg mt-4 mb-2">Gegevensoverdraagbaarheid (AVG art. 20)</h4>
      <p>Je kunt een machine-leesbare kopie van je gegevens opvragen via de API-endpoint <code>/api/users/me/export-data</code> of door contact op te nemen via <a href="mailto:privacy@inburgertest.nl" class="text-primary hover:underline">privacy@inburgertest.nl</a>.</p>
    `
  },
  {
    title: "5. Gebruikersgedrag",
    content: `
      <h4 class="font-semibold text-lg mt-4 mb-2">Toegestaan</h4>
      <ul class="list-disc pl-6 space-y-1">
        <li>Persoonlijk gebruik voor examenvoorbereiding</li>
        <li>Aantekeningen maken tijdens oefenen</li>
        <li>Delen van je resultaten met anderen</li>
        <li>Feedback geven om het platform te verbeteren</li>
      </ul>
      <h4 class="font-semibold text-lg mt-4 mb-2">Niet Toegestaan</h4>
      <ul class="list-disc pl-6 space-y-1">
        <li>Account delen met andere personen</li>
        <li>Kopiëren of distribueren van examenvragen</li>
        <li>Reverse engineering van het platform</li>
        <li>Gebruik van geautomatiseerde tools of bots</li>
        <li>Misbruik van support kanalen</li>
      </ul>
      <p class="mt-4 text-error font-medium">Overtreding kan leiden tot directe opzegging van je account zonder terugbetaling.</p>
    `
  },
  {
    title: "6. Auteursrecht & Content",
    content: `
      <p>Alle examenvragen, uitleg, video's en andere lesmaterialen zijn eigendom van Inburgertest © 2026. Je mag deze niet kopiëren, distribueren of commercieel gebruiken zonder schriftelijke toestemming.</p>
      <h4 class="font-semibold text-lg mt-4 mb-2">Jouw content</h4>
      <p>Teksten en opnames die je maakt in schrijf- en spreekopdrachten blijven jouw eigendom. We gebruiken deze alleen om feedback te geven en ons beoordelingssysteem te verbeteren. Je content wordt nooit gedeeld met derden.</p>
      <h4 class="font-semibold text-lg mt-4 mb-2">Feedback</h4>
      <p>Suggesties en feedback die je geeft mogen door ons gebruikt worden om het platform te verbeteren zonder compensatie.</p>
    `
  },
  {
    title: "7. Disclaimer",
    content: `
      <p><strong>Belangrijke mededeling:</strong></p>
      <ul class="list-disc pl-6 space-y-2 mt-2">
        <li>Inburgertest is <strong>NIET</strong> gelieerd aan DUO, het College voor Toetsen en Examens, of staatsexamensnt2.nl</li>
        <li>Onze examens zijn <strong>oefentools</strong>, geen officiële certificering</li>
        <li>Slagen voor onze examens garandeert geen succes op het officiële staatsexamen</li>
        <li>We doen ons best om examens zo realistisch mogelijk te maken, maar kunnen niet garanderen dat vragen identiek zijn aan het officiële examen</li>
        <li>Examenregels en -formaten kunnen wijzigen; we proberen updates snel te verwerken</li>
      </ul>
    `
  },
  {
    title: "8. Contact & Juridisch",
    content: `
      <p>Voor vragen over deze voorwaarden, neem contact op:</p>
      <ul class="list-none space-y-2 mt-2">
        <li><strong>Juridische vragen:</strong> <a href="mailto:legal@inburgertest.nl" class="text-primary hover:underline">legal@inburgertest.nl</a></li>
        <li><strong>Privacy concerns:</strong> <a href="mailto:privacy@inburgertest.nl" class="text-primary hover:underline">privacy@inburgertest.nl</a></li>
        <li><strong>Algemene support:</strong> <a href="mailto:support@inburgertest.nl" class="text-primary hover:underline">support@inburgertest.nl</a></li>
      </ul>
      <div class="mt-6 p-4 bg-gray-100 rounded-lg">
        <p class="font-semibold">Bedrijfsgegevens:</p>
        <p>Inburgertest (in oprichting)</p>
        <p>Amsterdam, Nederland</p>
        <p class="mt-2">KVK-nummer: volgt na inschrijving Handelsregister</p>
        <p>BTW-nummer: volgt na toewijzing Belastingdienst</p>
      </div>
      <h4 class="font-semibold text-lg mt-6 mb-2">Toepasselijk recht</h4>
      <p>Op deze voorwaarden is uitsluitend Nederlands recht van toepassing. Geschillen worden voorgelegd aan de bevoegde rechter in Amsterdam, Nederland, onverminderd je recht als consument om een beroep te doen op de rechter van je woonplaats.</p>
    `
  }
];
